import API_CONFIG from '../config/apiConfig';
import { normalizeRole } from '../utils/roles';

const TOKEN_KEY = 'lumivix_auth_token';
const USER_KEY = 'lumivix_user';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  user: {
    id: number;
    username: string;
    role: string;
    fullName?: string;
    /** true setelah admin membuat/mereset sandi: wajib diganti sebelum memakai aplikasi. */
    mustChangePassword?: boolean;
  };
}

export interface User {
  id: number;
  username: string;
  role: string;
  fullName?: string;
  mustChangePassword?: boolean;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.login}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(error.message || 'Invalid credentials');
      }

      const data: LoginResponse = await response.json();
      const { access_token, user } = data;

      // Store token and user data
      this.setToken(access_token);
      this.setUser(user);

      return data;
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  }

  async changePassword(userId: number, currentPassword: string, newPassword: string): Promise<any> {
    const token = this.getToken();
    const response = await fetch(`${API_CONFIG.baseURL}/users/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ userId, currentPassword, newPassword })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Gagal mengubah password' }));
      const message = Array.isArray(error.message) ? error.message.join('; ') : error.message;
      throw new Error(message || 'Gagal mengubah password');
    }

    const data = await response.json();
    // Mengganti sandi mencabut token lama; server menerbitkan token baru supaya
    // sesi yang sedang berjalan tidak terputus.
    if (data?.access_token) {
      this.setToken(data.access_token);
    }
    return data;
  }

  logout(): void {
    clearAuth();
    // Cookie sesi gambar (HttpOnly) hanya bisa dihapus server. Tanpa ini, setelah
    // "Keluar" peramban yang sama masih bisa membaca daftar studi lewat /dicom-web
    // sampai cookie-nya kedaluwarsa. Pindah ke halaman login menunggu jawaban
    // server sebentar saja (maks. 1,5 dtk) agar Keluar tidak macet saat server mati.
    endServerSession().finally(() => {
      window.location.href = '/login';
    });
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  isAuthenticated(): boolean {
    const token = getToken();
    if (!token) return false;

    // Check if token is expired (basic check)
    return !isTokenExpired(token);
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      const user = JSON.parse(userStr);
      // Sesi yang dibuka sebelum server menormalkan peran bisa menyimpan nama lama (mis. DOKTER).
      return user && typeof user === 'object' && user.role
        ? { ...user, role: normalizeRole(user.role) }
        : user;
    } catch {
      return null;
    }
  }

  // Helper method to make authenticated API calls
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Auto-redirect on 401
    if (response.status === 401) {
      this.logout();
    }

    return response;
  }
}

/**
 * JWT memakai base64url ('-' dan '_', tanpa padding), sedangkan atob() hanya mengenal
 * base64 biasa. Tanpa konversi, token yang isinya kebetulan memuat '-' atau '_' (mis.
 * nama pengguna dengan karakter non-ASCII) dianggap rusak: pengguna langsung dilempar
 * kembali ke halaman login setiap kali masuk.
 */
export function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    // atob menghasilkan string biner (satu karakter per byte UTF-8); ubah ke teks sebenarnya.
    const json = decodeURIComponent(
      Array.from(binary, ch => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** true bila klaim exp sudah lewat, atau token tidak terbaca. */
export function isTokenExpired(token: string, now: number = Date.now()): boolean {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp)) return true;
  return now >= exp * 1000;
}

/** Minta server menghapus cookie sesi. Tidak pernah melempar dan tidak pernah menunggu lebih dari 1,5 dtk. */
export function endServerSession(timeoutMs = 1500): Promise<void> {
  try {
    const request = fetch(`${API_CONFIG.baseURL}/auth/logout`, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
    }).then(
      () => undefined,
      () => undefined
    );
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>(resolve => {
      timer = setTimeout(resolve, timeoutMs);
    });
    return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
  } catch {
    return Promise.resolve();
  }
}

// Helper functions
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export const authService = new AuthService();
export default authService;
